import { Container, Graphics, Sprite, Text, Texture, type TextStyleOptions } from 'pixi.js';
import gsap from 'gsap';
import { Scene } from '../engine/scene';
import { getContext } from '../engine/context';
import { Button } from '../ui/Button';
import { onKeyDown } from '../engine/input';
import { clamp } from '../engine/math';
import { GAME_HEIGHT, GAME_WIDTH } from '../config/tuning';
import { isMobile } from '../config/mobile';

interface TutorialPage {
  title: string;
  subtitle?: string;
  body: string[];
  highlight?: string;
  buildIllustration: (scene: TutorialScene, cx: number, cy: number) => Container[];
}

/**
 * TutorialScene — paginated "how to play" with custom illustrations per page.
 * Each illustration is composed of Pixi primitives (Sprite/Graphics/Text) wrapped
 * in a Container so the scene can destroy the whole thing on page change.
 */
export class TutorialScene extends Scene {
  private pageIndex = 0;
  private pageRoot: Container | null = null;
  private prevBtn!: Text;
  private nextBtn!: Text;
  private pageLabel!: Text;
  private dots: Graphics[] = [];
  private mobile = false;

  async onEnter(): Promise<void> {
    this.mobile = isMobile();
    const ctx = getContext();

    // Room background is the #bg-extended CSS layer.

    // Header ribbon
    const titleFs = this.mobile ? 42 : 56;
    const titleY = this.mobile ? 48 : 60;
    const header = this.makeText('HOW TO PLAY', GAME_WIDTH / 2, titleY, {
      fontFamily: 'Impact, sans-serif',
      fontSize: titleFs,
      fill: 0xffd23f,
      stroke: { color: 0x1a0d22, width: 5 },
      letterSpacing: 3,
    });
    header.anchor.set(0.5);
    this.addChild(header);

    // Subtle animated underline under the header
    const ulW = this.mobile ? 220 : 320;
    const underline = new Graphics()
      .rect(-ulW / 2, 0, ulW, 3)
      .fill({ color: 0x5fdbcf, alpha: 0.8 });
    underline.position.set(GAME_WIDTH / 2, titleY + (this.mobile ? 30 : 40));
    this.addChild(underline);
    gsap.to(underline, { alpha: 0.35, duration: 1.6, yoyo: true, repeat: -1, ease: 'sine.inOut' });

    // Content card — darker background, soft outer glow, rounded.
    const cardW = this.mobile ? GAME_WIDTH - 40 : 1320;
    const cardH = this.mobile ? 720 : 700;
    const cardY = this.mobile ? GAME_HEIGHT / 2 + 50 : GAME_HEIGHT / 2 + 40;

    // Outer glow (soft drop shadow)
    const glow = new Graphics()
      .roundRect(-cardW / 2 - 8, -cardH / 2 - 8, cardW + 16, cardH + 16, 24)
      .fill({ color: 0x5fdbcf, alpha: 0.12 });
    glow.position.set(GAME_WIDTH / 2, cardY);
    this.addChild(glow);

    const card = new Graphics()
      .roundRect(-cardW / 2, -cardH / 2, cardW, cardH, 20)
      .fill({ color: 0x12071e, alpha: 0.88 })
      .stroke({ color: 0x5fdbcf, width: 3, alpha: 0.95 });
    card.position.set(GAME_WIDTH / 2, cardY);
    this.addChild(card);

    // Inner border accent — thin line inset from the main frame
    const inner = new Graphics()
      .roundRect(-cardW / 2 + 8, -cardH / 2 + 8, cardW - 16, cardH - 16, 14)
      .stroke({ color: 0x5fdbcf, width: 1, alpha: 0.25 });
    inner.position.set(GAME_WIDTH / 2, cardY);
    this.addChild(inner);

    this.buildNavControls();
    this.buildDots();
    this.renderPage();

    // Keyboard shortcuts
    this.onCleanup(onKeyDown('ArrowLeft', () => this.changePage(-1)));
    this.onCleanup(onKeyDown('ArrowRight', () => this.changePage(1)));
    this.onCleanup(onKeyDown('Escape', () => ctx.sceneManager.start('MenuScene')));

    // Swipe support on mobile
    if (this.mobile) {
      this.eventMode = 'static';
      let touchStartX = 0;
      this.on('pointerdown', (e: { global: { x: number } }) => { touchStartX = e.global.x; });
      this.on('pointerup', (e: { global: { x: number } }) => {
        const dx = e.global.x - touchStartX;
        if (Math.abs(dx) > 60) this.changePage(dx < 0 ? 1 : -1);
      });
    }
  }

  // ── Page definitions ─────────────────────────────────────────────────────

  private getPages(): TutorialPage[] {
    const mobile = this.mobile;
    return [
      {
        title: 'THE GOAL',
        subtitle: 'Longest total drag wins.',
        body: mobile ? [
          'You and the bot share a single joint.',
          'Hold the button to inhale — each second',
          'is banked as score when the joint passes.',
          'Choke or burn the end? Combo forfeited.',
          'Highest total when the joint is gone wins.',
        ] : [
          'You and the bot share a single joint.',
          'Every second you hold SPACE goes into your TURN COMBO — banked',
          'only when the joint passes cleanly to the bot. Choke, empty your',
          'lungs, or burn the cardboard and the whole combo is forfeited.',
          'When the joint is gone, highest banked total wins.',
        ],
        highlight: 'Most banked seconds = victory.',
        buildIllustration: (_s, cx, cy) => {
          const objs: Container[] = [];
          const sc = mobile ? 0.4 : 0.55;
          const dist = mobile ? 160 : 220;
          const p = this.makeSprite('player_body', cx - dist, cy + 30); p.scale.set(sc); objs.push(p);
          const ph = this.makeSprite('player_head_idle', cx - dist, cy - 55 * (mobile ? 0.7 : 1)); ph.scale.set(sc); objs.push(ph);
          const a = this.makeSprite('ai_body', cx + dist, cy + 30); a.scale.set(sc); objs.push(a);
          const ah = this.makeSprite('ai_head_idle', cx + dist, cy - 55 * (mobile ? 0.7 : 1)); ah.scale.set(sc); objs.push(ah);
          const jw = mobile ? 130 : 180;
          const jointPaper = this.makeSprite('joint_paper', cx, cy - 40);
          this.resizeSprite(jointPaper, jw, 18);
          objs.push(jointPaper);
          const filter = this.makeSprite('joint_filter', cx + jw / 2 - 5, cy - 40);
          this.resizeSprite(filter, mobile ? 24 : 32, 18);
          objs.push(filter);
          const ember = this.makeSprite('joint_ember_idle', cx - jw / 2 + 5, cy - 40);
          ember.scale.set(0.7);
          objs.push(ember);

          const fs = mobile ? 22 : 28;
          objs.push(this.makeText('YOU', cx - dist, cy + (mobile ? 80 : 130), {
            fontFamily: 'Impact, sans-serif', fontSize: fs, fill: 0x5fdbcf,
          }, true));
          objs.push(this.makeText('BOT', cx + dist, cy + (mobile ? 80 : 130), {
            fontFamily: 'Impact, sans-serif', fontSize: fs, fill: 0xff9ab5,
          }, true));
          return objs;
        },
      },
      {
        title: 'TAKING A DRAG',
        subtitle: mobile ? 'Hold the button to inhale.' : 'Hold SPACE to inhale. Release to exhale.',
        body: mobile ? [
          'While holding, the drag timer ticks up.',
          'Release safely → seconds go into your combo.',
          'They become SCORE when the joint passes.',
          '✔ Clean release: seconds added to combo.',
          '✘ Choke / empty lungs: ENTIRE combo lost.',
        ] : [
          'Every moment SPACE is held, the drag timer ticks up.',
          'Release safely and the seconds go into your turn combo (At Risk).',
          'They become SCORE only when the joint passes to the bot.',
          '✔︎  Clean release: seconds added to your turn combo.',
          '✘  Choke / empty lungs / burn cardboard: ENTIRE combo forfeited.',
        ],
        highlight: 'Watch the At Risk counter.',
        buildIllustration: (_s, cx, cy) => {
          const objs: Container[] = [];
          const head = this.makeSprite('player_head_inhale', cx - (mobile ? 110 : 160), cy + 10);
          head.scale.set(mobile ? 0.6 : 0.8);
          objs.push(head);
          const arrow = this.makeText('→', cx - 20, cy + 10, {
            fontFamily: 'Impact, sans-serif', fontSize: mobile ? 44 : 56, fill: 0xffd23f,
          }, true);
          objs.push(arrow);
          if (mobile) {
            const btnBg = new Graphics()
              .circle(cx + 120, cy + 10, 54)
              .fill({ color: 0x1a0d22, alpha: 0.85 })
              .stroke({ color: 0x5fdbcf, width: 3, alpha: 0.9 });
            objs.push(btnBg);
            objs.push(this.makeText('🌬', cx + 120, cy + 6, {
              fontFamily: 'system-ui', fontSize: 32, fill: 0xffffff,
            }, true));
            objs.push(this.makeText('hold to inhale', cx + 120, cy + 76, {
              fontFamily: 'Inter, sans-serif', fontSize: 16, fill: 0x88e8db,
            }, true));
          } else {
            const keyBg = new Graphics()
              .roundRect(cx + 150 - 110, cy + 10 - 45, 220, 90, 8)
              .fill({ color: 0x2a1f3a })
              .stroke({ color: 0x5fdbcf, width: 4 });
            objs.push(keyBg);
            objs.push(this.makeText('SPACE', cx + 150, cy + 10, {
              fontFamily: 'Impact, sans-serif', fontSize: 48, fill: 0xffd23f,
            }, true));
            objs.push(this.makeText('hold to inhale', cx + 150, cy + 80, {
              fontFamily: 'Inter, sans-serif', fontSize: 16, fill: 0x88e8db,
            }, true));
          }
          return objs;
        },
      },
      {
        title: 'LUNGS',
        subtitle: 'They deplete as you inhale.',
        body: mobile ? [
          'Lungs drain while inhaling. Recover slowly.',
          'Combo window: lungs recover FAST (~50% back).',
          '⚠️ Shorter joint → lungs drain up to 3× faster.',
          '❌ Lungs at ZERO = cough → whole combo lost.',
          'Orange/red bar = danger zone. Release early!',
        ] : [
          'Lungs drain while holding SPACE. Recover slowly between turns.',
          'In the combo window (right after release) they recover FAST.',
          '⚠️  As the joint gets SHORTER, lungs drain FASTER (up to 3× faster).',
          '❌  If lungs hit ZERO mid-drag you cough — the WHOLE turn combo is lost.',
          'The lung bar shifts orange → red and pulses as you near empty.',
        ],
        highlight: 'Big drags early, small drags late.',
        buildIllustration: (_s, cx, cy) => {
          const objs: Container[] = [];
          const frame = this.makeSprite('hud_lung_frame', cx, cy - 60);
          objs.push(frame);
          const fill = this.makeSprite('hud_lung_fill_mask', cx - 150, cy - 60, [0, 0.5]);
          this.resizeSpriteWidth(fill, 210);
          objs.push(fill);

          const yOff = mobile ? 30 : 40;
          const rows: Array<{ label: string; color: number; bar: number; speed: string; speedColor: number }> = [
            { label: 'Joint 100%', color: 0x88e8db, bar: 0x5fdbcf, speed: '1×', speedColor: 0x79e8a3 },
            { label: 'Joint 50%', color: 0x88e8db, bar: 0xffd23f, speed: '2×', speedColor: 0xffd23f },
            { label: 'Joint 10%', color: 0x88e8db, bar: 0xff4444, speed: '≈3×', speedColor: 0xff6b35 },
          ];
          rows.forEach((row, idx) => {
            const y = cy + (mobile ? 15 : 0) + idx * yOff;
            objs.push(this.makeText(row.label, cx - (mobile ? 200 : 260), y, {
              fontFamily: 'Inter, sans-serif', fontSize: 16, fill: row.color,
            }, false, [0, 0.5]));
            const bar = new Graphics()
              .rect(cx - (mobile ? 60 : 80), y - 6, mobile ? 150 : 200, 12)
              .fill({ color: row.bar });
            objs.push(bar);
            objs.push(this.makeText(`${row.speed} drain`, cx + (mobile ? 100 : 140), y, {
              fontFamily: 'Impact, sans-serif', fontSize: 18, fill: row.speedColor,
            }, false, [0, 0.5]));
          });
          return objs;
        },
      },
      {
        title: 'CHOKE METER',
        subtitle: 'Rises across your WHOLE turn.',
        body: mobile ? [
          'Fills while inhaling — does NOT reset between drags.',
          'Only resets when the joint passes to the bot.',
          '0–50%: safe · 50–70%: mild · 70–90%: heavy · 90%+: danger',
          '❌ Choke = ENTIRE turn combo lost — every drag gone.',
        ] : [
          'The meter fills while SPACE is held, and STAYS filled during combos.',
          'It only resets when the joint passes to the bot (end of turn).',
          '0–50%: safe.  50–70%: mild risk.  70–90%: heavy risk.  90%+: near-certain.',
          'At 70%+ the screen tints red and the choke bar starts pulsing.',
          '❌  If you choke, the ENTIRE turn\'s combo is forfeited — every drag lost.',
        ],
        highlight: 'Pass at yellow, never chase red.',
        buildIllustration: (_s, cx, cy) => {
          const objs: Container[] = [];
          const frameScale = mobile ? 1.2 : 1.6;
          const frame = this.makeSprite('hud_choke_frame', cx, cy - 60);
          frame.scale.set(frameScale);
          objs.push(frame);
          const fillW = 224 * frameScale;
          const fill = this.makeSprite('hud_choke_fill_mask', cx - fillW / 2, cy - 60, [0, 0.5]);
          this.resizeSpriteWidth(fill, fillW);
          objs.push(fill);

          const zones: Array<[string, number, number, number]> = [
            ['0–50%', 0x79e8a3, 0, 50],
            ['50–70%', 0xffd23f, 50, 70],
            ['70–90%', 0xff6b35, 70, 90],
            ['90%+', 0xff1a1a, 90, 100],
          ];
          const barLeft = cx - fillW / 2;
          zones.forEach(([label, color, lo, hi]) => {
            const midX = barLeft + fillW * ((lo + hi) / 2) / 100;
            objs.push(this.makeText(label, midX, cy + (mobile ? -20 : 14), {
              fontFamily: 'Impact, sans-serif', fontSize: mobile ? 14 : 16, fill: color,
            }, true));
          });

          const chokeHead = this.makeSprite('player_head_choke', cx + (mobile ? 180 : 260), cy + 80);
          chokeHead.scale.set(mobile ? 0.45 : 0.55);
          objs.push(chokeHead);
          objs.push(this.makeText('Choke = COMBO gone', cx - (mobile ? 30 : 60), cy + 80, {
            fontFamily: 'Impact, sans-serif', fontSize: mobile ? 20 : 22, fill: 0xff4444,
          }, true));
          return objs;
        },
      },
      {
        title: 'THE JOINT',
        subtitle: 'Shared. Finite. Red = cardboard.',
        body: mobile ? [
          'Burns down while ANYONE inhales.',
          'Last 12% = cardboard filter (RED zone).',
          'Inhaling when joint hits zero = match ends',
          'AND your combo is forfeited. Don\'t burn it!',
          'Pass early if you\'re winning — play safe.',
        ] : [
          'The joint burns down whenever ANYONE inhales (shared resource).',
          'The last 12% is the cardboard filter — the RED zone on the top bar.',
          'If you\'re the one inhaling when the joint hits zero (cardboard burn)',
          'your whole turn combo is forfeited AND the match ends.',
          'If the other player depletes it, their turn\'s combo is wiped instead.',
        ],
        highlight: 'Don\'t be left holding an empty roach.',
        buildIllustration: (_s, cx, cy) => {
          const objs: Container[] = [];
          const barW = mobile ? 500 : 700;
          const h = 24;
          const cardboardW = barW * 0.12;
          const bar = new Graphics()
            .rect(cx - barW / 2, cy - 60 - h / 2, barW, h)
            .fill({ color: 0x0a060f })
            .stroke({ color: 0xfce0bd, width: 3 });
          objs.push(bar);
          const safeRect = new Graphics()
            .rect(cx - barW / 2 + cardboardW, cy - 60 - (h - 6) / 2, barW - cardboardW - 2, h - 6)
            .fill({ color: 0xffd23f });
          objs.push(safeRect);
          const cardRect = new Graphics()
            .rect(cx - barW / 2 + 2, cy - 60 - (h - 6) / 2, cardboardW - 2, h - 6)
            .fill({ color: 0xff3838 });
          objs.push(cardRect);

          const labelSize = mobile ? 14 : 16;
          objs.push(this.makeText('CARDBOARD', cx - barW / 2 + cardboardW / 2, cy - (mobile ? 88 : 100), {
            fontFamily: 'Impact, sans-serif', fontSize: labelSize, fill: 0xff6b35,
          }, true));
          objs.push(this.makeText('SAFE ZONE', cx + 80, cy - (mobile ? 88 : 100), {
            fontFamily: 'Impact, sans-serif', fontSize: labelSize, fill: 0xffd23f,
          }, true));

          if (!mobile) {
            const paper = this.makeSprite('joint_paper', cx - 50, cy + 40);
            this.resizeSprite(paper, 260, 22);
            objs.push(paper);
            const filter = this.makeSprite('joint_filter', cx + 98, cy + 40);
            this.resizeSprite(filter, 46, 22);
            objs.push(filter);
            const ember = this.makeSprite('joint_ember_inhale', cx - 182, cy + 40);
            ember.scale.set(0.7);
            objs.push(ember);
          }
          return objs;
        },
      },
      {
        title: 'COMBO WINDOW',
        subtitle: '2.5 sec. High risk, high reward.',
        body: mobile ? [
          'After releasing, a ring appears on the joint.',
          'Tap again before it closes = another drag.',
          'Lungs recover FAST in the window (~50% back).',
          '⚠️ Choke on drag #3 wipes drags #1 & #2 too.',
          'Ignore the ring = turn passes, score is safe.',
        ] : [
          'After you release SPACE, a ring appears around the joint.',
          'Press SPACE again before it runs out to take another drag (combo).',
          'Lungs recover FAST during the window (~50% back). Choke meter does NOT.',
          '⚠️  Drags in a combo are ONLY banked when the turn passes.',
          '     A choke on drag #3 wipes drags #1 and #2 too.',
          'Ignore the ring and the turn passes automatically (score banked safely).',
        ],
        highlight: 'Watch the At Risk counter.',
        buildIllustration: (_s, cx, cy) => {
          const objs: Container[] = [];
          const ring = this.makeSprite('hud_combo_ring', cx, cy + 20);
          ring.scale.set(mobile ? 0.65 : 0.85);
          objs.push(ring);
          gsap.to(ring, { rotation: Math.PI * 2, duration: 3.5, repeat: -1, ease: 'none' });

          const tipFs = mobile ? 20 : 26;
          const tipOffset = mobile ? 220 : 320;
          objs.push(this.makeText('RELEASE →', cx - tipOffset, cy + 20, {
            fontFamily: 'Impact, sans-serif', fontSize: tipFs, fill: 0x5fdbcf,
          }, true));
          objs.push(this.makeText('← TAP AGAIN', cx + tipOffset, cy + 20, {
            fontFamily: 'Impact, sans-serif', fontSize: tipFs, fill: 0xffd23f,
          }, true));
          return objs;
        },
      },
      {
        title: 'THE PERFECT HIT',
        subtitle: mobile ? 'Time the release at 15% lungs.' : 'A skill bar appears when lungs get low. Release at the perfect moment.',
        body: mobile ? [
          'Start an inhale with FULL lungs (75%+)',
          'to arm the bonus challenge.',
          'When lungs drop to 30%, a bar appears.',
          'A line sweeps right → left as you drain.',
          '🎯 Release in the GOLDEN CENTER (~15% lungs)',
          '→ Choke meter DROPS by 5–25%. Free breathing room!',
          'Miss the zone? No penalty — try again next inhale.',
        ] : [
          'When you start an inhale with FULL lungs (75%+), a bonus skill-check',
          'is armed for that inhale. As lungs drain past 30%, a QTE bar appears.',
          'A line sweeps right → left, its speed tied to your lung drain rate',
          '(shorter joint = faster sweep). Release SPACE while the line is in',
          'the GOLDEN CENTER — that lines up with lungs at ~15%.',
          '🎯  Perfect hit → choke meter DROPS by 5–25%. Free breathing room.',
          'Miss the zone or never release? No penalty — eligibility stays armed',
          'until you attempt, so keep trying across inhales until you nail it.',
        ],
        highlight: 'Hit golden → choke shrinks. Free extra drags.',
        buildIllustration: (_s, cx, cy) => {
          const objs: Container[] = [];
          const barW = mobile ? 360 : 520;
          const barH = mobile ? 28 : 36;
          const barY = cy - 40;
          const barLeft = cx - barW / 2;

          // Track (dark bar background)
          const track = new Graphics()
            .roundRect(barLeft, barY - barH / 2, barW, barH, 6)
            .fill({ color: 0x0a060f, alpha: 0.9 })
            .stroke({ color: 0xfce0bd, width: 3, alpha: 0.75 });
          objs.push(track);

          // Golden zone (centered, ±6%)
          const zoneW = barW * 0.12;
          const zoneX = cx - zoneW / 2;
          const goldZone = new Graphics()
            .roundRect(zoneX, barY - barH / 2 - 4, zoneW, barH + 8, 4)
            .fill({ color: 0xffd23f, alpha: 0.85 })
            .stroke({ color: 0xffffff, width: 2 });
          objs.push(goldZone);
          gsap.to(goldZone, { alpha: 0.65, duration: 0.9, yoyo: true, repeat: -1, ease: 'sine.inOut' });

          // Sweep line animating across the bar
          const lineH = barH + 16;
          const line = new Graphics()
            .rect(-2, -lineH / 2, 4, lineH)
            .fill({ color: 0xff4466 });
          line.position.set(barLeft + barW, barY);
          objs.push(line);
          gsap.to(line, {
            x: barLeft,
            duration: 1.6,
            ease: 'none',
            repeat: -1,
            yoyo: false,
            onRepeat: () => { line.x = barLeft + barW; },
          });

          // "15%" label above golden zone with lung icon
          const lungLabelY = barY - barH / 2 - (mobile ? 26 : 32);
          objs.push(this.makeText('≈ 15% LUNGS', cx, lungLabelY, {
            fontFamily: 'Impact, sans-serif', fontSize: mobile ? 18 : 22, fill: 0xffd23f,
            stroke: { color: 0x1a0d22, width: 3 },
          }, true));

          // Endpoint labels
          const endFs = mobile ? 14 : 16;
          objs.push(this.makeText('100%', barLeft, barY + barH / 2 + (mobile ? 18 : 22), {
            fontFamily: 'Inter, sans-serif', fontSize: endFs, fill: 0x88e8db,
          }, true));
          objs.push(this.makeText('0%', barLeft + barW, barY + barH / 2 + (mobile ? 18 : 22), {
            fontFamily: 'Inter, sans-serif', fontSize: endFs, fill: 0xff6b6b,
          }, true));

          // Chevron below the zone pointing up to "release here"
          const ptrY = barY + barH / 2 + (mobile ? 42 : 52);
          const chev = new Graphics()
            .moveTo(cx - 10, ptrY).lineTo(cx, ptrY - 12).lineTo(cx + 10, ptrY)
            .stroke({ color: 0xffd23f, width: 4, cap: 'round', join: 'round' });
          objs.push(chev);
          objs.push(this.makeText('RELEASE HERE', cx, ptrY + (mobile ? 16 : 20), {
            fontFamily: 'Impact, sans-serif', fontSize: mobile ? 18 : 22, fill: 0xffd23f,
          }, true));

          // Reward sparkle on the right
          if (!mobile) {
            const sparkleX = barLeft + barW + 90;
            const sparkle = this.makeSprite('particle_sparkle', sparkleX, barY);
            sparkle.scale.set(2.2);
            objs.push(sparkle);
            gsap.to(sparkle.scale, { x: 2.8, y: 2.8, duration: 0.7, yoyo: true, repeat: -1, ease: 'sine.inOut' });
            objs.push(this.makeText('-CHOKE', sparkleX, barY + 44, {
              fontFamily: 'Impact, sans-serif', fontSize: 20, fill: 0x79e8a3,
            }, true));
          }
          return objs;
        },
      },
      {
        title: 'TIPS TO WIN',
        subtitle: 'Hard-earned wisdom.',
        body: mobile ? [
          '★ Long drags early — lungs drain normally then.',
          '★ Shorten drags as the joint burns down.',
          '★ Choke meter carries across combos. Pass early.',
          '★ Check the bot\'s total. Trail? Risk. Lead? Safe.',
          '★ NARCO mode calculates everything. Good luck.',
        ] : [
          '★  Take LONG drags early — lungs drain normally when joint is full.',
          '★  Shorten drags as the joint shortens — the drain ramps up fast.',
          '★  Combos compound: each extra drag banks more AND risks more.',
          '★  Choke meter carries across combos. Pass at yellow, never chase red.',
          '★  Watch the At Risk counter — that\'s exactly what you forfeit on failure.',
          '★  Watch the bar colors: lung turns orange/red, choke pulses when dangerous.',
          '★  Avoid the cardboard unless you\'re losing badly.',
          '★  Check the bot\'s total. Trail? Take risks. Lead? Play safe.',
          '★  NARCO mode: plans with score deltas, choke EV, and lung math.',
          '     It will NOT make mistakes. Good luck.',
        ],
        buildIllustration: (_s, cx, cy) => {
          const objs: Container[] = [];
          const count = mobile ? 6 : 8;
          const rx = mobile ? 180 : 240;
          const ry = mobile ? 75 : 100;
          for (let i = 0; i < count; i++) {
            const sp = this.makeSprite(
              'particle_sparkle',
              cx + Math.cos((i / count) * Math.PI * 2) * rx,
              cy + Math.sin((i / count) * Math.PI * 2) * ry
            );
            sp.scale.set(mobile ? 1.2 : 1.5);
            gsap.to(sp.scale, {
              x: mobile ? 1.6 : 2.0, y: mobile ? 1.6 : 2.0,
              duration: 0.8 + i * 0.15,
              yoyo: true, repeat: -1, ease: 'sine.inOut',
            });
            gsap.to(sp, {
              alpha: 1,
              duration: 0.8 + i * 0.15,
              yoyo: true, repeat: -1, ease: 'sine.inOut',
            });
            sp.alpha = 0.6;
            objs.push(sp);
          }
          return objs;
        },
      },
    ];
  }

  // ── Navigation ───────────────────────────────────────────────────────────

  private buildNavControls(): void {
    const ctx = getContext();
    const mobile = this.mobile;
    const arrowFs = mobile ? 64 : 80;
    const arrowX = mobile ? 44 : 70;
    const arrowY = GAME_HEIGHT / 2 + (mobile ? 40 : 30);
    const arrowBgR = mobile ? 40 : 52;

    // Circular backdrops so the arrows don't feel like floating text
    const prevBg = new Graphics()
      .circle(arrowX, arrowY, arrowBgR)
      .fill({ color: 0x1a0d22, alpha: 0.75 })
      .stroke({ color: 0x5fdbcf, width: 3 });
    this.addChild(prevBg);

    this.prevBtn = this.makeText('‹', arrowX, arrowY - 4, {
      fontFamily: 'Impact, sans-serif', fontSize: arrowFs, fill: 0x5fdbcf,
    }, true);
    this.prevBtn.eventMode = 'static';
    this.prevBtn.cursor = 'pointer';
    this.prevBtn.on('pointerover', () => { this.prevBtn.style.fill = 0xffd23f; });
    this.prevBtn.on('pointerout', () => { this.prevBtn.style.fill = 0x5fdbcf; });
    this.prevBtn.on('pointerdown', () => this.changePage(-1));
    this.addChild(this.prevBtn);

    const nextBg = new Graphics()
      .circle(GAME_WIDTH - arrowX, arrowY, arrowBgR)
      .fill({ color: 0x1a0d22, alpha: 0.75 })
      .stroke({ color: 0x5fdbcf, width: 3 });
    this.addChild(nextBg);

    this.nextBtn = this.makeText('›', GAME_WIDTH - arrowX, arrowY - 4, {
      fontFamily: 'Impact, sans-serif', fontSize: arrowFs, fill: 0x5fdbcf,
    }, true);
    this.nextBtn.eventMode = 'static';
    this.nextBtn.cursor = 'pointer';
    this.nextBtn.on('pointerover', () => { this.nextBtn.style.fill = 0xffd23f; });
    this.nextBtn.on('pointerout', () => { this.nextBtn.style.fill = 0x5fdbcf; });
    this.nextBtn.on('pointerdown', () => this.changePage(1));
    this.addChild(this.nextBtn);

    this.pageLabel = this.makeText('', GAME_WIDTH / 2, GAME_HEIGHT - (mobile ? 88 : 110), {
      fontFamily: 'Inter, sans-serif', fontSize: mobile ? 16 : 18, fill: 0x88889a,
    }, true);
    this.addChild(this.pageLabel);

    const menuBtn = new Button({
      x: GAME_WIDTH / 2,
      y: GAME_HEIGHT - (mobile ? 44 : 50),
      idleKey: 'ui_btn_menu',
      hoverKey: 'ui_btn_menu_hover',
      onClick: () => ctx.sceneManager.start('MenuScene'),
      audio: ctx.audio,
    });
    menuBtn.scale.set(mobile ? 0.65 : 0.75);
    this.addChild(menuBtn);
  }

  private buildDots(): void {
    const pages = this.getPages();
    const dotSpacing = this.mobile ? 20 : 26;
    const startX = GAME_WIDTH / 2 - ((pages.length - 1) * dotSpacing) / 2;
    const dotY = GAME_HEIGHT - (this.mobile ? 115 : 140);
    for (let i = 0; i < pages.length; i++) {
      const dot = new Graphics()
        .circle(startX + i * dotSpacing, dotY, this.mobile ? 5 : 6)
        .fill({ color: 0x5fdbcf, alpha: 0.35 });
      dot.eventMode = 'static';
      dot.cursor = 'pointer';
      const targetIndex = i;
      dot.on('pointerdown', () => {
        const delta = targetIndex - this.pageIndex;
        if (delta !== 0) this.changePage(delta);
      });
      this.dots.push(dot);
      this.addChild(dot);
    }
  }

  private changePage(delta: number): void {
    const pages = this.getPages();
    const next = clamp(this.pageIndex + delta, 0, pages.length - 1);
    if (next === this.pageIndex) return;
    this.pageIndex = next;
    getContext().audio.play('sfx_click', { volume: 0.55 });
    this.renderPage();
  }

  private renderPage(): void {
    if (this.pageRoot) {
      this.removeChild(this.pageRoot);
      this.pageRoot.destroy({ children: true });
    }
    this.pageRoot = new Container();
    this.pageRoot.sortableChildren = true;
    this.addChild(this.pageRoot);

    const pages = this.getPages();
    const page = pages[this.pageIndex];
    const mobile = this.mobile;

    const cx = GAME_WIDTH / 2;
    const titleY = mobile ? 170 : 230;
    const titleFs = mobile ? 36 : 48;

    const title = this.makeText(page.title, cx, titleY, {
      fontFamily: 'Impact, sans-serif',
      fontSize: titleFs,
      fill: 0xffd23f,
      stroke: { color: 0x1a0d22, width: 4 },
      letterSpacing: 2,
    }, true);
    this.pageRoot.addChild(title);

    // Decorative sub-title divider
    if (page.subtitle) {
      const subY = titleY + (mobile ? 38 : 48);
      const sub = this.makeText(page.subtitle, cx, subY, {
        fontFamily: 'Inter, sans-serif',
        fontSize: mobile ? 17 : 20,
        fill: 0x88e8db,
        fontStyle: 'italic',
        wordWrap: true,
        wordWrapWidth: mobile ? GAME_WIDTH - 120 : 1000,
        align: 'center',
      }, true);
      this.pageRoot.addChild(sub);
    }

    const illustrationY = mobile ? titleY + 160 : titleY + 210;
    const illustrationObjs = page.buildIllustration(this, cx, illustrationY);
    illustrationObjs.forEach((o) => this.pageRoot!.addChild(o));

    const bodyY = mobile ? illustrationY + 160 : illustrationY + 210;
    const lineH = mobile ? 28 : 32;
    const bodyFs = mobile ? 17 : 20;
    const wrapW = mobile ? GAME_WIDTH - 120 : 1180;

    page.body.forEach((line, i) => {
      const t = this.makeText(line, cx, bodyY + i * lineH, {
        fontFamily: 'Inter, sans-serif',
        fontSize: bodyFs,
        fill: 0xf5e9d6,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: wrapW,
        leading: 2,
      }, true);
      this.pageRoot!.addChild(t);
    });

    if (page.highlight) {
      const highlightY = bodyY + page.body.length * lineH + (mobile ? 20 : 26);
      // Pill behind the highlight for emphasis
      const hlText = page.highlight;
      const approxW = Math.min(wrapW, hlText.length * (mobile ? 13 : 15) + 40);
      const pill = new Graphics()
        .roundRect(cx - approxW / 2, highlightY - (mobile ? 18 : 22), approxW, mobile ? 36 : 44, 22)
        .fill({ color: 0xffd23f, alpha: 0.15 })
        .stroke({ color: 0xffd23f, width: 2, alpha: 0.7 });
      this.pageRoot.addChild(pill);
      const hl = this.makeText(hlText, cx, highlightY, {
        fontFamily: 'Impact, sans-serif',
        fontSize: mobile ? 20 : 24,
        fill: 0xffd23f,
        letterSpacing: 1,
      }, true);
      this.pageRoot.addChild(hl);
    }

    const navHintText = mobile
      ? `${this.pageIndex + 1} / ${pages.length}  —  swipe or tap arrows`
      : `Page ${this.pageIndex + 1} of ${pages.length}  —  use ← → or click the arrows`;
    this.pageLabel.text = navHintText;
    this.dots.forEach((d, i) => {
      d.clear();
      const active = i === this.pageIndex;
      const dotSpacing = this.mobile ? 20 : 26;
      const startX = GAME_WIDTH / 2 - ((pages.length - 1) * dotSpacing) / 2;
      const dotY = GAME_HEIGHT - (this.mobile ? 115 : 140);
      const r = active ? (this.mobile ? 7 : 9) : (this.mobile ? 5 : 6);
      d.circle(startX + i * dotSpacing, dotY, r).fill({
        color: active ? 0xffd23f : 0x5fdbcf,
        alpha: active ? 1 : 0.35,
      });
      if (active) {
        d.circle(startX + i * dotSpacing, dotY, r + 3).stroke({ color: 0xffd23f, width: 2, alpha: 0.45 });
      }
    });
    this.prevBtn.alpha = this.pageIndex === 0 ? 0.3 : 1;
    this.nextBtn.alpha = this.pageIndex === pages.length - 1 ? 0.3 : 1;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private makeSprite(key: string, x: number, y: number, anchor: [number, number] = [0.5, 0.5]): Sprite {
    const s = new Sprite(Texture.from(key));
    s.anchor.set(anchor[0], anchor[1]);
    s.position.set(x, y);
    return s;
  }

  private makeText(
    text: string,
    x: number,
    y: number,
    style: TextStyleOptions,
    centerAnchor = false,
    customAnchor?: [number, number]
  ): Text {
    const t = new Text({ text, style });
    if (customAnchor) t.anchor.set(customAnchor[0], customAnchor[1]);
    else if (centerAnchor) t.anchor.set(0.5);
    t.position.set(x, y);
    return t;
  }

  private resizeSprite(sprite: Sprite, width: number, height: number): void {
    const tex = sprite.texture;
    sprite.scale.set(width / tex.width, height / tex.height);
  }

  private resizeSpriteWidth(sprite: Sprite, width: number): void {
    const tex = sprite.texture;
    sprite.scale.set(width / tex.width, 1);
  }
}
